// TESSERAX — primitives de grille PURES (sans THREE ni DOM), testables unitairement.
// Extraites de main.ts à comportement identique, plus settleToward() qui ajoute
// la gravité DIRECTIONNELLE (cœur de la mécanique signature « Bascule »).
//
// La grille est grid[x][y][z] avec x,z dans [0,GRID) et y dans [0,HEIGHT) ;
// une cellule vide vaut null, sinon une couleur (number). y = hauteur (toujours).
import { GRID, HEIGHT } from './constants';

export type Cell = number | null;
export type Grid = Cell[][][];
// Direction de tassement horizontal pour la Bascule :
//   'nx' → vers x=0, 'px' → vers x=GRID-1, 'nz' → vers z=0, 'pz' → vers z=GRID-1.
export type Dir = 'nx' | 'px' | 'nz' | 'pz';

export function emptyGrid(): Grid {
  const g: Grid = [];
  for (let x = 0; x < GRID; x++) { g[x] = []; for (let y = 0; y < HEIGHT; y++) { g[x][y] = []; for (let z = 0; z < GRID; z++) g[x][y][z] = null; } }
  return g;
}

export function boardEmpty(grid: Grid): boolean {
  for (let x = 0; x < GRID; x++) for (let y = 0; y < HEIGHT; y++) for (let z = 0; z < GRID; z++) if (grid[x][y][z] !== null) return false;
  return true;
}

export function isLayerFull(grid: Grid, y: number): boolean {
  for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) if (grid[x][y][z] === null) return false;
  return true;
}

// `xRows`/`zRows` = décompte par AXE des lignes pleines (xRows = lignes alignées
// selon X, à z constant ; zRows = selon Z, à x constant). `rowCount = xRows + zRows`.
// Sert à récompenser la maîtrise propre à la 3D : effacer les DEUX axes d'un coup
// (une « Croix »), feat impossible dans un Tetris plan. Champs ADDITIFS — les
// consommateurs existants ne lisent que rowCount/rowCells/fullLayers (compatibles).
export interface ClearEval { fullLayers: number[]; rowCells: number[][]; rowCount: number; xRows: number; zRows: number; }

// Détecte les lignes pleines (7 en X ou en Z) et les étages pleins (49 cases).
// PERF-CORE-007 : on évite l'allocation d'un objet `fset` en testant fullLayers.includes(y)
// (tableau de ≤14 entiers — lookup O(n) négligeable pour HEIGHT=14).
export function evaluateClears(grid: Grid): ClearEval {
  const fullLayers: number[] = [];
  for (let y = 0; y < HEIGHT; y++) if (isLayerFull(grid, y)) fullLayers.push(y);
  const rowCells: number[][] = []; const seen = new Set<number>(); let xRows = 0, zRows = 0;
  // À l'intersection d'une CROIX, une cellule appartient à la fois à la ligne X et à la
  // ligne Z pleines → elle était poussée DEUX FOIS dans rowCells (double flash de particules,
  // consommateurs trompés). On dédoublonne via une clé entière y*49 + x*7 + z. Aucune incidence
  // sur le score (rowCount = xRows+zRows, calcul séparé) ni sur la sim (le clear teste null).
  const push = (x: number, y: number, z: number) => { const key = (y * GRID + x) * GRID + z; if (!seen.has(key)) { seen.add(key); rowCells.push([x, y, z]); } };
  for (let y = 0; y < HEIGHT; y++) {
    if (fullLayers.includes(y)) continue;
    // Ligne alignée sur X (parcourt x à z fixe) → compte dans xRows.
    for (let z = 0; z < GRID; z++) { let f = true; for (let x = 0; x < GRID; x++) if (grid[x][y][z] === null) { f = false; break; } if (f) { xRows++; for (let x = 0; x < GRID; x++) push(x, y, z); } }
    // Ligne alignée sur Z (parcourt z à x fixe) → compte dans zRows.
    for (let x = 0; x < GRID; x++) { let f = true; for (let z = 0; z < GRID; z++) if (grid[x][y][z] === null) { f = false; break; } if (f) { zRows++; for (let z = 0; z < GRID; z++) push(x, y, z); } }
  }
  return { fullLayers, rowCells, rowCount: xRows + zRows, xRows, zRows };
}

// Une « Croix » : au moins une ligne sur CHAQUE axe horizontal effacée d'un seul
// coup. C'est l'expression de skill propre au puits 3D. Pur → testable & réutilisé
// par le score (bonus Croix) et le juice.
export function isCrossClear(ev: ClearEval): boolean { return ev.xRows > 0 && ev.zRows > 0; }

// Copie profonde d'une grille (pour les prévisualisations « et si ? » non destructives).
export function cloneGrid(grid: Grid): Grid {
  const g: Grid = [];
  for (let x = 0; x < GRID; x++) { g[x] = []; for (let y = 0; y < HEIGHT; y++) { g[x][y] = grid[x][y].slice(); } }
  return g;
}

// PRÉVISUALISATION DE CLEAR : si l'on verrouillait `addCells` ([x,y,z] ou [x,y,z,color])
// sur la grille, quelles lignes/étages se videraient ? N'altère PAS `grid`. Sert au
// fantôme d'atterrissage pour répondre à « ce coup va-t-il casser ? » AVANT le verrou.
export function previewClears(grid: Grid, addCells: number[][]): ClearEval {
  const g = cloneGrid(grid);
  for (let i = 0; i < addCells.length; i++) {
    const c = addCells[i]; const x = c[0], y = c[1], z = c[2];
    if (x >= 0 && x < GRID && y >= 0 && y < HEIGHT && z >= 0 && z < GRID) g[x][y][z] = (c[3] !== undefined ? c[3] : 1);
  }
  return evaluateClears(g);
}

// Liste plate des cellules posées [x,y,z,color] (pour dessiner une pile prévisualisée).
export function occupiedCells(grid: Grid): number[][] {
  const out: number[][] = [];
  for (let x = 0; x < GRID; x++) for (let y = 0; y < HEIGHT; y++) for (let z = 0; z < GRID; z++) { const v = grid[x][y][z]; if (v !== null) out.push([x, y, z, v]); }
  return out;
}

export interface BasculePreview {
  moved: number[][]; occupied: number[][]; clearCells: number[][];
  rows: number; slabs: number; chain: number;
  holesBefore: number; holesAfter: number; repairedHoles: number;
  heightBefore: number; heightAfter: number; drop: number;
}

function maxHeight(grid: Grid): number {
  let m = 0;
  for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) {
    for (let y = HEIGHT - 1; y >= 0; y--) if (grid[x][y][z] !== null) { if (y + 1 > m) m = y + 1; break; }
  }
  return m;
}

// PRÉVISUALISATION DE BASCULE : sur une COPIE, tasse vers `dir`, détecte les clears
// immédiats, puis rejoue la résolution EXACTE du moteur (r1 → gravité → r2) pour
// estimer la chaîne. N'altère PAS `grid`. Cœur de l'aperçu « pourquoi Bascule est fort ».
// PERF-CORE-004 : `withOccupied=false` permet à bestBascule de sauter occupiedCells
// (scan complet 7×14×7) inutile pour la seule décision de cascade.
export function previewBascule(grid: Grid, dir: Dir, sealColor?: number, withOccupied = true): BasculePreview {
  const g = cloneGrid(grid);
  const holesBefore = holeCount(g), heightBefore = maxHeight(g);
  const moved = settleToward(g, dir, sealColor);
  const occupied = withOccupied ? occupiedCells(g) : [];  // état de la pile juste après le glissement
  // Clears IMMÉDIATS formés par la compaction (mis en évidence dans l'aperçu).
  const ev0 = evaluateClears(g);
  const clearCells: number[][] = ev0.rowCells.slice();
  for (let i = 0; i < ev0.fullLayers.length; i++) { const y = ev0.fullLayers[i]; for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) clearCells.push([x, y, z]); }
  // MIROIR EXACT de la résolution du moteur (sim.doBascule / rules.doBascule) :
  // r1 = resolveBoard → applyGravity → r2 = resolveBoard, chain = max(r1, r2),
  // rows/slabs additifs. La passe de gravité INTERMÉDIAIRE est décisive : quand la
  // compaction seule ne complète rien (r1 vide), ce sont les blocs en surplomb qui,
  // en RETOMBANT, ferment les lignes — une boucle unique qui s'arrête au premier
  // tour sans clear rate ces cascades (le cue « CASCADE PRÊTE » se taisait
  // exactement quand le payoff était monté, cf. tests/grid.test.ts).
  const resolvePass = (): { rows: number; slabs: number; chain: number } => {
    let rows = 0, slabs = 0, chain = 0;
    while (true) {
      const e = evaluateClears(g);
      if (e.rowCount === 0 && e.fullLayers.length === 0) break;
      chain++;
      for (let i = 0; i < e.rowCells.length; i++) { const c = e.rowCells[i]; g[c[0]][c[1]][c[2]] = null; }
      for (let i = 0; i < e.fullLayers.length; i++) { const y = e.fullLayers[i]; for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) g[x][y][z] = null; }
      settleDown(g);
      rows += e.rowCount; slabs += e.fullLayers.length;
    }
    return { rows, slabs, chain };
  };
  const r1 = resolvePass();
  settleDown(g);                               // l'applyGravity inter-passes du moteur
  const r2 = resolvePass();
  const rows = r1.rows + r2.rows, slabs = r1.slabs + r2.slabs, chain = Math.max(r1.chain, r2.chain);
  const holesAfter = holeCount(g), heightAfter = maxHeight(g);
  return { moved, occupied, clearCells, rows, slabs, chain, holesBefore, holesAfter, repairedHoles: Math.max(0, holesBefore - holesAfter), heightBefore, heightAfter, drop: Math.max(0, heightBefore - heightAfter) };
}

// Une option de Bascule retenue par le scan ci-dessous (direction + butin de cascade estimé).
export interface BasculeOption { dir: Dir; chain: number; rows: number; slabs: number; }

// MEILLEURE BASCULE DISPONIBLE : évalue les 4 murs (previewBascule) et renvoie celui qui
// déclenche la plus forte CASCADE — à chaîne égale, le plus de grilles, puis de lignes. Renvoie
// null si AUCUN mur n'efface quoi que ce soit. Sert à INVITER le joueur, pendant le jeu normal,
// quand une grosse cascade est PRÉPARÉE et n'attend qu'un tilt (cf. main.ts updateCascadeCue) :
// la Bascule devient un PAYOFF qu'on monte, pas seulement un correctif réactif. Pur — chaque
// previewBascule travaille sur une COPIE, la grille n'est jamais mutée → testable, déterministe.
const BASCULE_DIRS: Dir[] = ['nx', 'px', 'nz', 'pz'];
export function bestBascule(grid: Grid, sealColor?: number): BasculeOption | null {
  let best: BasculeOption | null = null;
  for (let i = 0; i < BASCULE_DIRS.length; i++) {
    const dir = BASCULE_DIRS[i];
    // PERF-CORE-004 : on ne calcule pas occupiedCells — bestBascule n'utilise que chain/rows/slabs.
    const p = previewBascule(grid, dir, sealColor, false);
    if (p.chain === 0 && p.rows === 0 && p.slabs === 0) continue;          // ce mur n'efface rien
    if (!best || p.chain > best.chain ||
        (p.chain === best.chain && (p.slabs > best.slabs ||
        (p.slabs === best.slabs && p.rows > best.rows)))) best = { dir, chain: p.chain, rows: p.rows, slabs: p.slabs };
  }
  return best;
}

// Nombre de colonnes (x,z) présentant au moins un « trou » (vide recouvert d'un bloc).
export function holeCount(grid: Grid): number {
  let n = 0;
  for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) { let seen = false, hole = false; for (let y = HEIGHT - 1; y >= 0; y--) { if (grid[x][y][z] !== null) seen = true; else if (seen) { hole = true; break; } } if (hole) n++; }
  return n;
}

// Cases « trou » : cellules VIDES recouvertes par ≥1 bloc plus haut dans la même colonne
// (x,z). Dans un puits 3D plein, un trou enfoui est INVISIBLE de l'extérieur → le joueur ne
// sait pas où dégager (le downstacking, compétence de survie clé, devient aveugle). holeCount
// compte les colonnes trouées ; ici on renvoie les CASES [x,y,z] à marquer (cf. main.ts
// rebuildHoles : marqueur « rayon X » lisible à travers la pile). Pur, n'altère pas la grille.
export function holeCells(grid: Grid): number[][] {
  const out: number[][] = [];
  for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) {
    let cover = false;
    for (let y = HEIGHT - 1; y >= 0; y--) { if (grid[x][y][z] !== null) cover = true; else if (cover) out.push([x, y, z]); }
  }
  return out;
}

// Gravité verticale : tasse chaque colonne (x,z) vers le bas. Mute la grille en place.
export function settleDown(grid: Grid): void {
  for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) {
    let w = 0;
    for (let y = 0; y < HEIGHT; y++) { const v = grid[x][y][z]; if (v !== null) { grid[x][y][z] = null; grid[x][w][z] = v; w++; } }
  }
}

// Gravité HORIZONTALE (Bascule) : tasse les cellules posées vers le mur `dir`,
// à hauteur Y constante. Mute la grille en place. Renvoie les cellules déplacées
// (pour le juice) sous forme [x,y,z,color] à leur position d'ARRIVÉE.
// `sealColor` (optionnel) : couleur des cellules « scellées » (cf. montée
// d'instabilité en profondeur). Une cellule scellée est une ANCRE : la Bascule ne la
// déplace PAS et les cellules ne peuvent pas la traverser → elle laisse un trou que le
// tassement horizontal ne comble pas (on ne peut la vider qu'en complétant sa rangée
// par empilement). Absent (défaut) → comportement historique inchangé.
export function settleToward(grid: Grid, dir: Dir, sealColor?: number): number[][] {
  const moved: number[][] = [];
  const isSeal = (v: Cell): boolean => sealColor !== undefined && v === sealColor;
  if (dir === 'nx' || dir === 'px') {
    const toMax = dir === 'px';
    for (let y = 0; y < HEIGHT; y++) for (let z = 0; z < GRID; z++) {
      let w = toMax ? GRID - 1 : 0; const step = toMax ? -1 : 1;
      for (let i = 0; i < GRID; i++) { const x = toMax ? GRID - 1 - i : i; const v = grid[x][y][z]; if (v === null) continue; if (isSeal(v)) { w = x + step; continue; } if (x !== w) { grid[x][y][z] = null; grid[w][y][z] = v; moved.push([w, y, z, v]); } w += step; }
    }
  } else {
    const toMax = dir === 'pz';
    for (let x = 0; x < GRID; x++) for (let y = 0; y < HEIGHT; y++) {
      let w = toMax ? GRID - 1 : 0; const step = toMax ? -1 : 1;
      for (let i = 0; i < GRID; i++) { const z = toMax ? GRID - 1 - i : i; const v = grid[x][y][z]; if (v === null) continue; if (isSeal(v)) { w = z + step; continue; } if (z !== w) { grid[x][y][z] = null; grid[x][y][w] = v; moved.push([x, y, w, v]); } w += step; }
    }
  }
  return moved;
}

// MONTÉE D'INSTABILITÉ : insère un bloc « parasite » au pied de CHAQUE colonne
// listée dans `fill` ([x,z]), en poussant cette colonne d'un cran vers le haut
// (y → y+1). N'agit QUE colonne par colonne (les colonnes hors `fill` sont
// intactes) : ainsi la pile reste tassée et la gravité verticale du jeu ne
// défait pas la montée au prochain clear. Mute la grille en place. Renvoie true
// si un bloc plein a été poussé HORS du puits (par le haut) → top-out (le puits
// déborde, la run se termine). Cœur de la pression croissante (cf. core/pressure.ts).
export function riseFloor(grid: Grid, fill: number[][], color: number): boolean {
  let toppedOut = false;
  for (let i = 0; i < fill.length; i++) {
    const c = fill[i], x = c[0], z = c[1];
    if (x < 0 || x >= GRID || z < 0 || z >= GRID) continue;
    if (grid[x][HEIGHT - 1][z] !== null) toppedOut = true;       // colonne pleine → débordement
    for (let y = HEIGHT - 1; y > 0; y--) grid[x][y][z] = grid[x][y - 1][z];
    grid[x][0][z] = color;
  }
  return toppedOut;
}
